import {Component, inject, OnInit, output, signal, WritableSignal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MovePictureDialogProjectFragment} from "@app/platform/components/pictures/move-picture-dialog/move-picture-dialog.generated"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {IsNonNull} from "@cm/utils/filter"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {UtilsService} from "@legacy/helpers/utils"
import {MovePictureProjectsGQL} from "@platform/components/pictures/move-picture-dialog/move-picture-dialog.generated"
import {from} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-move-picture-dialog",
    templateUrl: "./move-picture-dialog.component.html",
    styleUrls: ["./move-picture-dialog.component.scss"],
    imports: [MatDialogContent, MatDialogTitle, MatInputModule, FormsModule, MatSelectModule, MatDialogActions, MatButtonModule],
})
export class MovePictureDialogComponent implements OnInit {
    readonly onConfirm = output<MoveCopyPictureDialogResult>()
    readonly $project = signal<MovePictureDialogProjectFragment | undefined>(undefined)
    readonly $projects = signal<MovePictureDialogProjectFragment[]>([])
    readonly $action = signal<Action | undefined>(undefined)
    actions = Action
    readonly $result: WritableSignal<MoveCopyPictureDialogResult>
    readonly $loadingSets = signal(false)

    readonly projectsGql = inject(MovePictureProjectsGQL)
    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to
    readonly dialogRef = inject(MatDialogRef<MovePictureDialogComponent>)
    readonly utils = inject(UtilsService)
    readonly snackBar = inject(MatSnackBar)
    readonly organizations = inject(OrganizationsService)
    readonly data = inject<MoveCopyPictureDialogResult & {action: Action}>(MAT_DIALOG_DATA)

    constructor() {
        this.$action.set(this.data.action)
        this.$result = signal<MoveCopyPictureDialogResult>({
            organizationId: this.data.organizationId,
            setId: this.data.setId,
        })
    }

    ngOnInit(): void {
        const result = this.$result()
        if (result) this.loadProjects(result.organizationId)
    }

    loadProjects(organizationId: string): void {
        from(fetchThrowingErrors(this.projectsGql)({filter: {organizationId: {equals: organizationId}}, take: 1000})).subscribe({
            next: ({projects}) => {
                this.$projects.set(projects.filter(IsNonNull))
                for (const project of this.$projects()) {
                    for (const set of project.sets) {
                        if (this.$result()?.setId === set.id) {
                            this.$project.set(project)
                        }
                    }
                }
            },
            error: () => this.snackBar.open("Cannot load projects.", "", {duration: 3000}),
        })
    }

    confirmAction(): void {
        const result = this.$result()
        if (result) {
            this.onConfirm.emit(result)
        }
        this.dialogRef.close(true)
    }

    cancelAction(): void {
        this.dialogRef.close(false)
    }

    updateResultOrganizationId(organizationId: string): void {
        this.$result.update((r) => ({...r, organizationId}))
    }

    updateResultSetId(setId: string | undefined): void {
        this.$result.update((r) => ({...r, setId}))
    }
}

export enum Action {
    MOVE_PICTURE,
    COPY_SCENE,
}

export type MoveCopyPictureDialogResult = {
    organizationId: string
    setId: string | undefined
}
